<template>
  <div class="reorder-variant">
    <img :src="image" alt="Variant Image" />

    <favorite-button :id="productId" :is-fav="isFavorite" :is-product="true" v-if="!deleted" />

    <div class="variant-content">
      <h3 class="product-name">{{ productName }}</h3>
      <div class="variant-name">{{ variantName }}</div>

      <template v-if="!deleted">
        <div class="variant-picker-container" v-if="!isOutOfStock">
          <single-variant-picker
            class="variant-picker"
            v-model="quantityInputValue"
            :decrease-quantity="decreaseQuantity"
            :disable-decrease-button="disableDecreaseButton(quantityInputValue, minimum)"
            :increase-quantity="increaseQuantity"
            :increment="Number(increment)"
            :quantity-input-blur="onQuantityInputBlur"
          />
          <div class="sum-price">${{ sumPrice }}</div>
        </div>

        <div class="quantity-warning" v-if="inventoryQuantityWarning && !isOutOfStock">
          {{ `Only ${inventoryQuantity} left in stock.` }}
        </div>

        <div v-if="showMinimumQuantityWarning" class="quantity-warning">
          You need to add at least {{ minimum }} of this item.
        </div>

        <bulletin-button
          full-width
          variant="primary"
          class="reorder-button"
          @click="addToCart"
          :disabled="reorderDisabled || isOutOfStock"
          >{{ isOutOfStock ? "Out of stock" : "Reorder" }}</bulletin-button
        >
      </template>

      <div class="quantity-warning" v-show="deleted">No longer available.</div>

      <div class="flex justify-center">
        <router-link class="pdp-link underline" :to="pdpLink">See all variants</router-link>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/sass/base/_vars.scss";
@import "@/assets/sass/base/_mixins.scss";

.reorder-variant {
  width: 100%;
  position: relative;
  font-size: 0;

  @include respond-to("small") {
    min-width: calc(75vw - 16px);
  }

  img {
    width: 100%;
    max-height: 500px;
    object-fit: cover;
  }

  .variant-content {
    padding: 24px;
    background: $secondaryGrey;

    @include respond-to("small") {
      padding: 16px;
    }

    .product-name {
      font-size: 18px;
    }

    .variant-name {
      font-family: NHaasGroteskDSPro-55Rg;
      font-size: 12px;
      margin-top: 8px;
    }

    .reorder-button {
      margin-top: 24px;
    }
  }

  .variant-picker-container {
    margin-top: 16px;
    min-height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .sum-price {
      font-family: NHaasGroteskDSPro-55Rg;
      font-size: 16px;
      margin-top: 10px;
    }
  }

  .quantity-warning {
    margin-top: 16px;
    font-size: 12px;
    color: $errorRed;
    font-family: NHaasGroteskDSPro-55Rg;
  }

  .pdp-link {
    font-size: 16px;
    font-family: NHaasGroteskDSPro-55Rg;
    margin-top: 16px;
  }
}
</style>

<script>
import { variantPickerHelper } from "../../../components/shared/variantPicker/variantPickerHelper";

export default {
  mixins: [variantPickerHelper],
  data: () => {
    return {
      quantityInputValue: 0,
    };
  },
  props: {
    image: {
      type: String,
      default: null,
    },
    productName: {
      type: String,
      default: "",
    },
    variantName: {
      type: String,
      default: "",
    },
    price: {
      type: Number,
      default: 0,
    },
    originalQuantity: {
      type: Number,
      default: 0,
    },
    minimum: {
      type: Number,
      default: 1,
    },
    increment: {
      type: Number,
      default: 1,
    },
    isFavorite: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: String,
      default: "",
    },
    variantId: {
      type: String,
      default: "",
    },
    trackInventory: {
      type: Boolean,
      default: true,
    },
    inStock: {
      type: Boolean,
      default: true,
    },
    inventoryQuantity: {
      type: Number,
      default: 0,
    },
    deleted: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.quantityInputValue = this.originalQuantity;
  },
  computed: {
    reorderDisabled() {
      if (this.inventoryQuantityWarning) {
        return true;
      }

      const remainder = this.quantityInputValue % this.increment;
      return remainder !== 0 || this.quantityInputValue < this.minimum;
    },
    inventoryQuantityWarning() {
      return this.trackInventory && this.inventoryQuantity > 0 && this.quantityInputValue > this.inventoryQuantity;
    },
    isOutOfStock() {
      if (!this.trackInventory && !this.inStock) {
        return true;
      }

      if (this.trackInventory) {
        return !this.inventoryQuantity || this.minimum > this.inventoryQuantity;
      }
    },
    showMinimumQuantityWarning() {
      return this.quantityInputValue < this.minimum;
    },
    sumPrice() {
      return Number(this.quantityInputValue * this.price).toFixed(2);
    },
    pdpLink() {
      return {
        name: "productDetails",
        params: {
          productName: this.$utils.urlSafeString(this.productName),
          productId: this.productId,
        },
      };
    },
  },
  methods: {
    increaseQuantity,
    decreaseQuantity,
    onQuantityInputBlur,
    addToCart,
  },
};

function increaseQuantity() {
  this.quantityInputValue = this.increaseVariantQuantity(this.quantityInputValue, this.increment);
}

function decreaseQuantity() {
  this.quantityInputValue = this.decreaseVariantQuantity(this.quantityInputValue, this.increment, this.minimum);
}

function onQuantityInputBlur() {
  if (this.quantityInputValue < this.minimum) {
    this.quantityInputValue = this.minimum;
    return;
  }

  const currentValue = this.quantityInputValue;
  const newValue = this.roundsUpValueBasedOnIncrement(currentValue, this.increment);

  if (newValue !== currentValue) {
    this.quantityInputValue = newValue;
  }
}
function addToCart() {
  this.$eventBus.$emit("add-to-cart-from-reorder", this.variantId, this.quantityInputValue);
}
</script>
